import { animate, state, style, transition, trigger } from '@angular/animations';
import { SelectionModel } from '@angular/cdk/collections';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { FilterItem } from '../../../../projects/kiene-core/src/lib/services/backend-api/classes/filter-item';
import { KieneBackendApiService } from '../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { KieneFileService } from '../../../../projects/kiene-core/src/lib/services/files/kiene-file.service';
import { LocalStorageService } from '../../../../projects/kiene-core/src/lib/services/local-storage/local-storage.service';
import { environment } from '../../../environments/environment';
import { FileUploadService } from '../../dialogs/file-upload-dialog/file-upload.service';
import { JsonDialogComponent } from '../../dialogs/json-dialog/json-dialog.component';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { EntityViewerService } from '../entity-viewer/entity-viewer.service';
import { ColumnFilterDialogComponent } from './column-filter-dialog/column-filter-dialog.component';
import { KieneTableService } from './kiene-table.service';
import {
    ActionTableColumn,
    ArrayTableColumn,
    ConditionTableColumn,
    FunctionTableColumn,
    IfElseTableColumn,
    InputTableColumn,
    KieneBooleanTableColumn,
    LinkTableColumn,
    PdfTableColumn,
    SlideToggleTableColumn,
    StatusTableColumn,
    TableColumn,
} from './table-column';
import { SelectionToolbarButton, SpalteKonfiguration, TableDescriptor, ToolbarButton } from './table-descriptor';
import { TableOption } from './table-option';
import { Clipboard } from '@angular/cdk/clipboard';
import { KieneApi } from './kiene-api';

class TableSetting {
    tabelle: string;
    seitenindex: number;
    elemente_pro_seite: number;
    suchtext: string;
    sortierung: string;
}

@Component({
    selector: 'kiene-kiene-table',
    templateUrl: './kiene-table.component.html',
    styleUrls: ['./kiene-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    host: { '(window:keydown)': 'checkForKeyShortcut($event)' },
})
export class KieneTableComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    isLoading = false;
    initialSortDirection: SortDirection = 'asc';

    @ViewChild('searchInput') searchInput: MatInput;
    @ViewChild('tableScrollContainer') tableScrollContainer: ElementRef<HTMLDivElement>;
    @ViewChild('bottomScrollbar') bottomScrollbar: ElementRef<HTMLDivElement>;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    @ViewChild('floatingStickyHeader') floatingStickyHeader?: ElementRef<HTMLDivElement>;
    @ViewChild('floatingStickyHeaderInner') floatingStickyHeaderInner?: ElementRef<HTMLDivElement>;

    @Input() tableDescriptor: TableDescriptor = new TableDescriptor();
    @Input() kunde_id: number;

    tableDataSource = new MatTableDataSource();

    totalCount = 0;
    checkedFilterCount: number = null;

    onInitComplete = false;

    showColumnFilter = true;
    displayedColumns: string[] = [];
    spaltenKonfigurationen: SpalteKonfiguration[] = [];
    expandableSpaltenKonfigurationen: SpalteKonfiguration[] = [];
    private savedSpaltenKonfigurationen?: SpalteKonfiguration[];
    private savedExpandableSpaltenKonfigurationen?: SpalteKonfiguration[];

    externalParams: HttpParams;

    reloadSubscription: Subscription;
    currentClientSubscription: Subscription;
    loadDataSubscription: Subscription;
    loadDataTotalSubscription: Subscription;
    clearSelectionSubscription: Subscription;

    expandedIndexes: Array<number | string> = [];

    allowMultipleSelection = true;
    selection: SelectionModel<any>;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    searchCtrl = new UntypedFormControl();
    private unfilteredData: any[] = [];
    private filteredClientData: any[] = [];

    isMobileScreen = false;
    tableSettingSub: Subscription = new Subscription();
    private tableSettingSaveSub: Subscription = new Subscription();
    horizontalScrollWidth = 0;
    showHorizontalScrollbar = false;
    private syncFromTable = false;
    private syncFromBottom = false;
    private resizeObserver?: ResizeObserver;
    private bottomScrollbarMountedToBody = false;

    stickyHeaderVisible = false;
    stickyHeaderHeight = 0;
    stickyHeaderCells: { label: string; width: number; isOptions: boolean }[] = [];
    private stickyHeaderScrollParent: HTMLElement | null = null;
    private stickyHeaderRaf = 0;
    private stickyHeaderCellsBuilt = false;
    private readonly onStickyHeaderScroll = () => this.scheduleFloatingStickyHeaderUpdate();

    constructor(
        private readonly kieneTableService: KieneTableService,
        private readonly api: KieneBackendApiService,
        private readonly localStorageService: LocalStorageService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly fileUploadService: FileUploadService,
        public dialog: MatDialog,
        private readonly entityViewerService: EntityViewerService,
        private readonly breakpointObserver: BreakpointObserver,
        private readonly messageService: MessageService,
        private readonly fileService: KieneFileService,
        private readonly clipboard: Clipboard,
        private readonly renderer: Renderer2,
        private readonly hostElement: ElementRef<HTMLElement>,
        private readonly cdr: ChangeDetectorRef,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {
        this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape]).subscribe({
            next: result => {
                if (result.matches) {
                    this.isMobileScreen = true;
                } else {
                    this.isMobileScreen = false;
                }
            },
        });
    }

    ladeEntityInEntityViewer(row) {
        this.entityViewerService.setEntity(row);
    }

    ngOnInit() {
        this.loadTableSettings();

        this.reloadSubscription = this.kieneTableService.watchReload().subscribe(response => {
            this.reload(response);
        });

        this.kieneTableService.watchColumns().subscribe({
            next: () => {
                if (this.savedSpaltenKonfigurationen) {
                    this.initSpaltenKonfigurationen(false, this.savedSpaltenKonfigurationen);
                } else if (this.getColumnFilterIdentifier(false)) {
                    this.loadVisibleColumns();
                    return;
                } else {
                    this.initSpaltenKonfigurationen(false);
                }
                if (this.tableDescriptor?.expandable) {
                    if (this.savedExpandableSpaltenKonfigurationen) {
                        this.initExpandableSpaltenKonfigurationen(false, this.savedExpandableSpaltenKonfigurationen);
                    } else if (this.getColumnFilterIdentifier(true)) {
                        this.loadVisibleExpandableColumns();
                    } else {
                        this.initExpandableSpaltenKonfigurationen(false);
                    }
                }
            },
        });

        this.clearSelectionSubscription = this.kieneTableService.watchClearSelection().subscribe({
            next: () => {
                this.clearSelection();
            },
        });

        this.currentClientSubscription = this.localStorageService.watchCurrentClient().subscribe(() => {
            this.expandedIndexes = [];
            this.reload('watch current client');
        });

        this.tableDescriptor.defaultPageSize = this.localStorageService.getCurrentUser().elemente_pro_seite;

        if (this.tableDescriptor.showSearchBar && !this.tableDescriptor.clearSearch) {
            let searchFilterValue = this.localStorageService.getSearchFilterValue(this.tableDescriptor.headline);
            searchFilterValue = searchFilterValue ? searchFilterValue : '';

            this.searchCtrl.setValue(searchFilterValue, { emitEvent: false });
            this.kieneTableService.sendSearchTxt(searchFilterValue);
        }
        this.initialSortDirection = <SortDirection>this.tableDescriptor.initialSortDirection;

        this.sort.direction = <SortDirection>this.tableDescriptor.initialSortDirection;

        if (!this.isMobileScreen) {
            this.paginator.pageSize = this.localStorageService.getCurrentUser().elemente_pro_seite;
        }

        this.selection = new SelectionModel(this.allowMultipleSelection);

        this.onInitComplete = true;
    }

    ngOnChanges() {
        if (this.onInitComplete) {
            this.loadDataNew('on changes', false);
        }
    }

    ngAfterViewInit() {
        this.searchCtrl.valueChanges
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                tap(() => {
                    this.paginator.pageIndex = 0;
                    const search = this.searchCtrl.value ? this.searchCtrl.value : '';
                    if (this.tableDescriptor.showSearchBar) {
                        this.localStorageService.setSearchFilterValue(this.tableDescriptor.headline, search);
                    }
                    this.kieneTableService.sendSearchTxt(search);
                    if (this.tableDescriptor.clientSideSearch) {
                        this.applyClientSideSearch(search);
                        return;
                    }
                    this.loadDataNew('search', true);
                }),
            )
            .subscribe();

        // If the user changes the sort order, reset back to the first page.
        this.sort.sortChange.subscribe(() => {
            this.paginator.pageIndex = 0;
            if (this.tableDescriptor.clientSideSort) {
                this.sortCurrentData();
                return;
            }
            this.loadDataNew('sort change', true);
        });

        this.paginator.page.subscribe(() => {
            if (this.tableDescriptor.clientSideSearch) {
                this.updateClientSideVisibleData();
                return;
            }
            this.loadDataNew('page change', true);
        });

        this.mountBottomScrollbarToBody();
        this.initHorizontalScrollbarObserver();
        this.scheduleHorizontalScrollbarSync();
        this.initFloatingStickyHeader();
    }

    ngOnDestroy() {
        this.reloadSubscription?.unsubscribe();
        this.currentClientSubscription?.unsubscribe();
        this.clearSelectionSubscription?.unsubscribe();
        this.loadDataSubscription?.unsubscribe();
        this.loadDataTotalSubscription?.unsubscribe();
        this.tableSettingSub?.unsubscribe();
        this.tableSettingSaveSub?.unsubscribe();
        this.resizeObserver?.disconnect();
        this.unmountBottomScrollbarFromBody();
        this.destroyFloatingStickyHeader();
    }

    masterSelectionToggle(event: MatCheckboxChange) {
        if (event.checked) {
            this.selection?.clear();
            const oldPageIndex = this.paginator.pageIndex;
            const oldPageSize = this.paginator.pageSize;
            this.paginator.pageIndex = 0;
            this.paginator.pageSize = this.totalCount;
            const sub = this.loadDataNew('masterSelectionToggle', true, true).subscribe({
                next: result => {
                    result.forEach(elem => this.selection?.select(elem));
                    this.kieneTableService.setSelectedRows(this.selection?.selected);
                    sub.unsubscribe();
                    this.paginator.pageIndex = oldPageIndex;
                    this.paginator.pageSize = oldPageSize;
                },
            });
        } else {
            this.selection?.clear();
            this.kieneTableService.setSelectedRows(this.selection?.selected);
        }
    }

    isAllSelected() {
        return this.selection?.selected?.length >= this.totalCount;
    }

    isRowSelected(row: any) {
        const index = this.selection?.selected?.findIndex(
            elem => elem[this.tableDescriptor.nameOfIdField] === row[this.tableDescriptor.nameOfIdField],
        );
        if (index > -1) {
            return true;
        }
        return false;
    }

    changeSelection(event: MatCheckboxChange, row: any) {
        if (event.checked) {
            this.selection?.selected?.push(row);
        } else {
            const index = this.selection?.selected?.findIndex(
                elem => elem[this.tableDescriptor.nameOfIdField] === row[this.tableDescriptor.nameOfIdField],
            );
            this.selection?.selected?.splice(index, 1);
        }
        this.kieneTableService.setSelectedRows(this.selection?.selected);
    }

    checkForKeyShortcut(event: KeyboardEvent) {
        if (event.code === 'KeyN' && !this.isKeyEventFromInput(event)) {
            if (this.tableDescriptor.standardCreate && this.tableDescriptor.createAllowed) {
                this.createNew();
            } else if (!this.tableDescriptor.standardCreate && this.tableDescriptor.createAllowed) {
                this.clickedAdd();
            }
        }
        if (event.code === 'KeyF' && !this.isKeyEventFromInput(event)) {
            setTimeout(() => {
                this.searchInput?.focus();
            }, 10);
        }
    }

    clickedAdd() {
        if (this.tableDescriptor?.addVoid) {
            this.tableDescriptor.addVoid();
        }
    }

    getColumns() {
        const columns = this.tableDescriptor.columns;
        const newColumns: TableColumn[] = [];

        for (const c of columns) {
            if (c.name !== 'options' && c.name !== 'detailsButton' && c.isActive === true) {
                newColumns.push(c);
            }
        }
        return newColumns;
    }

    getAllColumnsLength() {
        let len = this.tableDescriptor.columns.length;
        if (this.tableDescriptor.options.length > 0) {
            len++;
        }
        if (this.tableDescriptor.expandable) {
            len++;
        }
        // entity viewer button
        if (this.localStorageService?.isCurrentUserPhitoUser()) {
            len++;
        }
        if (this.tableDescriptor.selectable) {
            len++;
        }

        return len;
    }

    reload(sender: string) {
        if (sender && !environment?.production) {
            console.log('reload data: %s', sender);
        }
        this.loadDataNew(sender, true);
    }

    download_excel() {
        this.fileService.downloadExcel(this.getUrl(), this.getParams(false), {
            columnsCommaSeparated: this.prepareColumnsForDownloads(),
        });
    }

    download_csv() {
        this.fileService.downloadCSV(this.getUrl(), this.getParams(false), {
            columnsCommaSeparated: this.prepareColumnsForDownloads(),
        });
    }

    loadDataNew(sender: string, reload?: boolean, onlyInBackground?: boolean): Observable<any[]> {
        const dataLoadFinishedSubject = new Subject<any[]>();

        if (this.getUrl().startsWith('undefined')) {
            return;
        }

        if (this.tableDescriptor.loadOnlyOnReload) {
            if (!reload) {
                return;
            }
        }

        if (sender && !environment?.production) {
            console.log('Request sender is %s', sender);
        }

        if (this.tableDescriptor.waitToLoad) {
            if (!environment?.production) {
                console.log('waittoload');
            }
            return;
        }

        const searchMin = this.tableDescriptor.searchMinLength || 0;
        const searchTxt = (this.searchCtrl?.value || '').toString().trim();
        if (searchMin > 0 && searchTxt.length < searchMin) {
            this.isLoading = false;
            this.totalCount = 0;
            this.tableDataSource.data = [];
            dataLoadFinishedSubject.next([]);
            return dataLoadFinishedSubject;
        }

        this.isLoading = true;

        const params = this.getParams(true);
        this.kieneTableService.setParamsChanged(this.tableDescriptor.uniqueIdentifier, params);

        const url = this.getUrl();

        this.loadDataSubscription?.unsubscribe();
        this.loadDataSubscription = this.api!.get(url, params).subscribe({
            next: (response: KieneApi) => {
                if (this.isPagePastEnd(response)) {
                    this.paginator.pageIndex = 0;
                    if (!this.tableDescriptor.clientSideSearch) {
                        this.loadDataNew(sender, reload, onlyInBackground);
                        return;
                    }
                }
                this.kieneTableService.setLastTableUrlAndParams(params);
                this.isLoading = false;
                this.kieneTableService.dataLoadedCompleted(response);
                this.applyAutoExpandRows(response?.records);
                if (!onlyInBackground) {
                    if (this.tableDescriptor.clientSideSearch) {
                        this.unfilteredData = response.records ?? [];
                        this.applyClientSideSearch(this.searchCtrl.value ? this.searchCtrl.value : '');
                    } else {
                        this.totalCount = response.count;
                        this.tableDataSource.data = response.records;
                    }
                    this.scheduleHorizontalScrollbarSync();
                }
                dataLoadFinishedSubject.next(response.records);
                this.savePageSettings();
            },
            error: error => {
                this.isLoading = false;
                this.messageService.errorMessage('Daten konnten nicht geladen werden!', error);
                dataLoadFinishedSubject.next(undefined);
            },
        });

        if (this.tableDescriptor.totalRow === true) {
            this.loadDataTotalSubscription?.unsubscribe();
            this.loadDataTotalSubscription = this.api
                .get(this.tableDescriptor.totalRowApiUrl, this.getParams(false))
                .subscribe({
                    next: (response: any) => {
                        this.kieneTableService.setCurrentTotalData(response);
                    },
                    error: err => {
                        this.messageService.errorMessage('Summenwerte konnten nicht geladen werden!', err);
                    },
                });
        }

        return dataLoadFinishedSubject;
    }

    getSortDirection() {
        if (this.sort?.direction != null) {
            return this.sort.direction;
        }
        return this.tableDescriptor.initialSortDirection;
    }

    updatedFilterItems(filterItems: FilterItem[]) {
        if (filterItems) {
            this.tableDescriptor.filterItems = filterItems;
            this.goToFirstPage();
            this.loadDataNew('updatedFilterItems', true);
        }
    }

    goToFirstPage(): void {
        if (this.paginator && this.paginator.pageIndex !== 0) {
            this.paginator.pageIndex = 0;
        }
    }

    hasPermissionForFilter(fi: FilterItem): boolean {
        if (fi.adminOnly) {
            if (!this.localStorageService.currentUserIsAdmin()) {
                return false;
            }
        }
        if (fi.permissionId) {
            return this.localStorageService.currentUserHasPermission(fi.permissionId);
        }
        return true;
    }

    getTableFilterParams(params: HttpParams): HttpParams {
        for (const fi of this.tableDescriptor.filterItems) {
            if (fi.checked && this.hasPermissionForFilter(fi)) {
                if (fi.value === null) {
                    params = params.append(fi.name + '[]', 'null');
                } else if (fi.value instanceof Array) {
                    for (let v of fi.value) {
                        params = params.append(fi.name + '[]', v);
                    }
                } else {
                    params = params.append(fi.name + '[]', fi.value);
                }
            }
        }
        return params;
    }

    createNew() {
        this.router.navigate(['neu'], { relativeTo: this.route });
    }

    slideToggled(column: TableColumn, row: any, event: MatSlideToggleChange) {
        const sc = <SlideToggleTableColumn>column;
        if (sc.void) {
            row[sc.name] = event.checked ? 1 : 0;
            sc.void(row);
        }
    }

    isRowDetailsClickable(row: any): boolean {
        if (!this.tableDescriptor.showDetailsForRow) {
            return true;
        }
        return this.tableDescriptor.showDetailsForRow(row);
    }

    showDetails(row: any) {
        if (!this.isRowDetailsClickable(row)) {
            return;
        }
        if (this.tableDescriptor.showDetailsVoid) {
            this.tableDescriptor.showDetailsVoid(row);
            return;
        }

        const id = row[this.tableDescriptor.nameOfIdField];
        const recposition = row.recposition;

        if (!this.tableDescriptor.showDetails || !id) {
            if (!id && !environment?.production) {
                console.log('the id value of that row is null or undefined!');
            }
            return;
        }

        const routeArray: any[] = [];
        if (this.tableDescriptor.detailsRoute) {
            routeArray.push(this.tableDescriptor.detailsRoute);
        }
        routeArray.push(id);
        this.router.navigate(routeArray, {
            relativeTo: this.route,
            queryParams: { recposition: recposition, orderby: this.getOrderBy(), search: this.searchCtrl.value },
        });
    }

    getOrderBy() {
        if (this.sort?.active && this.displayedColumns.indexOf(this.sort.active) < 0) {
            this.sort.active = this.tableDescriptor.initialSortColumn;
        }

        let o = '';
        for (const sc of this.getSortColumns()) {
            if (sc) {
                if (o != '') {
                    o += ',';
                }
                o += sc + ' ' + this.getSortDirection();
            }
        }
        return o;
    }

    hasAtLeastOneOptionPermission(): boolean {
        for (const o of this.tableDescriptor.options) {
            if (this.localStorageService.currentUserHasPermission(o.permissionId)) {
                return true;
            }
        }
        return false;
    }

    clickedPdf(tc: TableColumn, row: any) {
        const ptc = <PdfTableColumn>tc;
        if (ptc.void) {
            ptc.void(row);
        }
    }

    clickedTableOption(option: TableOption, row: any) {
        if (option?.void) {
            const clone = JSON.parse(JSON.stringify(row));
            option.void(clone);
        }
    }

    clickedSelectionToolbarButton(tb: SelectionToolbarButton) {
        if (tb.void) {
            tb.void(this.selection?.selected);
        }
    }

    getSelectedRows(): any[] {
        const rows = this.selection?.selected;
        return rows ? rows : [];
    }

    getStatusBackgroundColor(column: TableColumn, row: any) {
        const c: StatusTableColumn = <StatusTableColumn>column;
        const status = Number(row['status_id']);
        const colorMap = c.colorMap;
        let color = '#9e9e9e';

        if (colorMap && !Number.isNaN(status)) {
            colorMap.forEach((v: string, k: number) => {
                if (status === k) {
                    color = v;
                }
            });
        }
        return color;
    }

    uploadFiles(files: File[], row: any) {
        this.fileUploadService.uploadFilesToServer(
            files,
            this.tableDescriptor.nameOfIdField,
            row[this.tableDescriptor.nameOfIdField],
            this.tableDescriptor.fileUploadApi,
        );
    }

    getIconName(column: TableColumn, row: any) {
        const c: ConditionTableColumn = <ConditionTableColumn>column;
        return c.getIcon(row);
    }

    getIconColor(column: TableColumn, row: any) {
        const c: ConditionTableColumn = <ConditionTableColumn>column;
        return c.getColor(row);
    }

    getTooltip(column: TableColumn, row: any) {
        const c: ConditionTableColumn = <ConditionTableColumn>column;
        return c.getTooltip(row);
    }

    public getSumValue(field: string) {
        let v = this.getValue(this.kieneTableService.getCurrentTotalData(), field);
        if (v === null || v === undefined) {
            return null;
        }
        return '(' + v + ')';
    }

    public getValue(object: any, field: string): any {
        if (field && object) {
            if (field.includes('.')) {
                const fields: string[] = field.split('.');
                let obj = object;
                for (const f of fields) {
                    if (obj !== null && obj !== undefined) {
                        obj = obj[f];
                    }
                }
                return obj;
            } else {
                return object[field];
            }
        }
        return null;
    }

    copyToClipboard(row: any, column: string) {
        const value = this.getValue(row, column);
        this.clipboard.copy(value);
        this.messageService.infoMessage(value + ' in Zwischenablage kopiert!');
    }

    public getColor(object: any) {
        if (this.tableDescriptor.disableConditions.length <= 0) {
            return 'black';
        }
        let complied = true;
        for (const c of this.tableDescriptor.disableConditions) {
            complied = complied && c.check(object);
        }
        if (complied) {
            return 'lightgray';
        }
        return 'black';
    }

    shouldShowHintTooltip(column: TableColumn, row: any): boolean {
        if (!column?.hint) {
            return false;
        }
        const value = this.getValue(row, column.hint);
        if (value === null || value === undefined) {
            return false;
        }
        if (typeof value === 'string') {
            const textLength = value.trim().length;
            if (column.maxLength && column.maxLength > 0) {
                return textLength > column.maxLength;
            }
            const maxWidthRaw = (column?.maxWidth || '').trim();
            const maxWidthCh = Number(maxWidthRaw.replace('ch', '').trim());
            if (!Number.isNaN(maxWidthCh) && maxWidthCh > 0 && maxWidthRaw.endsWith('ch')) {
                return textLength > maxWidthCh;
            }
            const maxWidthPx = Number(maxWidthRaw.replace('px', '').trim());
            if (!Number.isNaN(maxWidthPx) && maxWidthPx > 0) {
                // Näherungswert: bei schmalen Spalten wird bereits bei deutlich weniger Zeichen abgeschnitten.
                const threshold = maxWidthPx <= 170 ? 18 : 35;
                return textLength > threshold;
            }
            return textLength > 35;
        }
        return true;
    }

    getTruncatedDisplayText(column: TableColumn, row: any): string {
        const field = column.hint || column.name;
        const value = this.getValue(row, field);
        if (value === null || value === undefined) {
            return '';
        }
        const text = String(value);
        if (column.maxLength && column.maxLength > 0 && text.length > column.maxLength) {
            return text.substring(0, column.maxLength) + '…';
        }
        return text;
    }

    getHintTooltip(column: TableColumn, row: any): string {
        if (!this.shouldShowHintTooltip(column, row)) {
            return '';
        }
        const value = this.getValue(row, column.hint);
        return value != null ? String(value) : '';
    }

    expand(row: any) {
        const expandKey = this.getExpandKey(row);
        if (expandKey === null) {
            return;
        }
        const existingIndex = this.expandedIndexes.findIndex(ei => ei === expandKey);
        if (existingIndex >= 0) {
            this.expandedIndexes.splice(existingIndex, 1);
        } else {
            this.expandedIndexes.push(expandKey);
            this.loadExpandableIfNeeded(row);
        }
    }

    private loadExpandableIfNeeded(row: any): void {
        const loader = this.tableDescriptor?.expandableLoad;
        if (!loader || !row) {
            return;
        }
        // Bereits erfolgreich geladen
        if (row.__expandLoaded) {
            return;
        }
        // Laufenden Request nicht doppelt starten
        if (row.__expandLoading) {
            return;
        }
        row.__expandLoading = true;
        this.cdr.markForCheck();

        const finish = (ok: boolean) => {
            row.__expandLoading = false;
            if (ok) {
                row.__expandLoaded = true;
            }
            // Mat-Table merkt Nested-Mutationen sonst oft nicht
            if (this.tableDataSource?.data) {
                this.tableDataSource.data = this.tableDataSource.data.slice();
            }
            this.cdr.detectChanges();
        };

        try {
            const result: any = loader(row);
            // Observable zuerst (HttpClient) – nicht über .then (interop/thenable)
            if (result && typeof result.subscribe === 'function') {
                result.subscribe({
                    next: () => finish(true),
                    error: (err: unknown) => {
                        console.error('expandableLoad failed', err);
                        this.messageService.errorMessage('Details konnten nicht geladen werden.', err as any);
                        finish(false);
                    },
                });
            } else if (result && typeof result.then === 'function') {
                result.then(
                    () => finish(true),
                    (err: unknown) => {
                        console.error('expandableLoad failed', err);
                        this.messageService.errorMessage('Details konnten nicht geladen werden.', err as any);
                        finish(false);
                    },
                );
            } else {
                finish(true);
            }
        } catch (err) {
            console.error('expandableLoad failed', err);
            row.__expandLoading = false;
            this.cdr.detectChanges();
        }
    }

    isExpanded(row: any) {
        const expandKey = this.getExpandKey(row);
        if (expandKey !== null && this.expandedIndexes.includes(expandKey)) {
            return true;
        }

        // Legacy-Fallback: einige Tabellen speichern initial noch numerische recposition-Werte.
        const recpositionNumber = Number(row?.recposition);
        if (!Number.isNaN(recpositionNumber) && this.expandedIndexes.includes(recpositionNumber)) {
            return true;
        }

        // Legacy-Fallback: einige Tabellen nutzen (oder speicherten) den sichtbaren Zeilenindex.
        const rows = this.tableDataSource?.data as any[];
        const rowIndex = rows?.indexOf(row);
        if (rowIndex >= 0) {
            if (this.expandedIndexes.includes(rowIndex)) {
                return true;
            }
            if (this.expandedIndexes.includes('index:' + String(rowIndex))) {
                return true;
            }
        }
        return false;
    }

    private getExpandKey(row: any): string | null {
        if (!row) {
            return null;
        }

        const idField = this.tableDescriptor?.nameOfIdField;
        const id = idField ? row[idField] : null;
        if (id !== null && id !== undefined && id !== '') {
            return 'id:' + String(id);
        }

        const recposition = row.recposition;
        if (recposition !== null && recposition !== undefined && recposition !== '') {
            return 'recposition:' + String(recposition);
        }

        const rows = this.tableDataSource?.data as any[];
        const fallbackIndex = rows?.indexOf(row);
        if (fallbackIndex >= 0) {
            return 'index:' + String(fallbackIndex);
        }

        return null;
    }

    getExpandableRows(row: any): any[] {
        const fieldName = this.tableDescriptor?.expandableFieldName;
        const value = fieldName ? this.getValue(row, fieldName) : undefined;
        if (row?.__expandLoading && !Array.isArray(value)) {
            return [];
        }
        return Array.isArray(value) ? value : [];
    }

    showExpandableTotal(row: any): boolean {
        const cols = this.tableDescriptor?.expandableSumColumns;
        return Array.isArray(cols) && cols.length > 0 && this.getExpandableRows(row).length > 0;
    }

    isExpandableSumColumn(tc: TableColumn): boolean {
        return !!tc && (this.tableDescriptor?.expandableSumColumns || []).includes(tc.name);
    }

    /** Summe einer Expand-Zahlenspalte; bei kosten_netto + buchungsseite H als Negativ. */
    getExpandableColumnSum(row: any, columnName: string): number {
        const rows = this.getExpandableRows(row);
        let sum = 0;
        for (const r of rows) {
            let v = Number(this.getValue(r, columnName));
            if (!Number.isFinite(v)) {
                continue;
            }
            if (columnName === 'kosten_netto') {
                const seite = String(this.getValue(r, 'buchungsseite') || 'S').toUpperCase();
                v = seite === 'H' ? -Math.abs(v) : Math.abs(v);
            }
            sum += v;
        }
        return Math.round(sum * 100) / 100;
    }

    executeCallback(row: any, column: TableColumn) {
        const c: FunctionTableColumn = <FunctionTableColumn>column;
        const value = this.getValue(row, c.name);
        return c.callback(value, row);
    }

    getFunctionFullText(column: TableColumn, row: any): string {
        const text = this.executeCallback(row, column);
        return text != null ? String(text) : '';
    }

    getFunctionDisplayText(column: TableColumn, row: any): string {
        const full = this.getFunctionFullText(column, row);
        if (column.maxLength && column.maxLength > 0 && full.length > column.maxLength) {
            return full.substring(0, column.maxLength) + '…';
        }
        return full;
    }

    shouldShowFunctionTooltip(column: TableColumn, row: any): boolean {
        const full = this.getFunctionFullText(column, row);
        if (!full) {
            return false;
        }
        return !!(column.maxLength && column.maxLength > 0 && full.length > column.maxLength);
    }

    ifElseValue(row: any, column: TableColumn) {
        const c: IfElseTableColumn = <IfElseTableColumn>column;
        const v = this.getValue(row, c.conditionAttribute);
        if (v) {
            return this.getValue(row, c.ifValue);
        } else {
            return this.getValue(row, c.elseValue);
        }
    }

    openJsonDialog(json: any) {
        this.dialog.open(JsonDialogComponent, { data: { json: json } });
    }

    public parseStringForUrl(text: string) {
        if (!text) {
            return null;
        }
        const urltext = text.replace(
            /(https?:\/\/(www\.)?)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&\/\/=]*)/g,
            '<a class="kiene-inline-link" href="$&">$&</a>',
        );
        return urltext;
    }

    getDateFromRawLocalDateTime(row: any, columnName: string) {
        const raw = this.getValue(row, columnName);
        if (!raw) {
            return null;
        }
        if (typeof raw === 'string') {
            return new Date(raw);
        } else {
            const date = new Date(raw.year, 0);
            return new Date(date.setDate(raw.dayOfYear));
        }
    }

    clickedOnExpandedTable(row: any) {
        if (!this.tableDescriptor.showDetailsExpandable) {
            return;
        }

        this.router.navigate([this.tableDescriptor.route, row[this.tableDescriptor.nameOfIdField]]).then();
    }

    showDownloads() {
        return (
            this.tableDescriptor?.showDownloads ||
            this.localStorageService?.currentUserIsAdmin() ||
            this.localStorageService?.currentUserHasPermission(this.tableDescriptor?.permissionDownload)
        );
    }

    saveInput(row, c: TableColumn) {
        const tc = <InputTableColumn>c;
        this.api
            .updateGeneric(tc.api_url + 'update.php', row)
            .subscribe({
                next: () => this.messageService.infoMessage('Zusatztext gespeichert!'),
                error: err => this.messageService.errorMessage('Fehler Zusatztext speichern: ', err),
            });
    }

    checkboxChanged($event: MatCheckboxChange, c: TableColumn, row: any) {
        const btc = <KieneBooleanTableColumn>c;
        if (btc.void) {
            btc.void(row);
        }
    }

    hasPermission(permissionId: number) {
        return this.localStorageService.hasPermission(permissionId);
    }

    clearSelection() {
        this.selection?.clear();
        this.kieneTableService.setSelectedRows(this.selection?.selected);
    }

    setCheckedFilterCount(count: number) {
        if (count > 0) {
            this.checkedFilterCount = count;
        } else {
            this.checkedFilterCount = null;
        }
    }

    createQueryParams(c: TableColumn, row) {
        const ltc = <LinkTableColumn>c;
        const p = {};
        if (ltc.params) {
            for (const k of ltc.params) {
                p[k] = this.getValue(row, k);
            }
            return p;
        }
        return undefined;
    }

    public openFilterDialog(forExpandableColumns = false): void {
        const cols = forExpandableColumns ? this.expandableSpaltenKonfigurationen : this.spaltenKonfigurationen;
        const identifier = this.getColumnFilterIdentifier(forExpandableColumns);
        if (!identifier) {
            return;
        }
        this.dialog
            .open(ColumnFilterDialogComponent, {
                data: { cols, identifier },
            })
            .afterClosed()
            .subscribe({
                next: value => {
                    if (value) {
                        if (forExpandableColumns) {
                            this.initExpandableSpaltenKonfigurationen(false, value);
                        } else {
                            this.initSpaltenKonfigurationen(false, value);
                        }
                    }
                },
            });
    }

    savePageSettings() {
        if (!this.tableDescriptor?.uniqueIdentifier) {
            return;
        }
        const tabellenEinstellung = new TableSetting();
        tabellenEinstellung.tabelle = this.tableDescriptor.uniqueIdentifier;
        tabellenEinstellung.seitenindex = this.tableDescriptor.persistPageIndex
            ? this.paginator?.pageIndex
            : 0;
        tabellenEinstellung.elemente_pro_seite = this.paginator?.pageSize;
        tabellenEinstellung.sortierung = (this.getOrderBy() || '').slice(0, 200);
        tabellenEinstellung.suchtext = (this.searchCtrl.value || '').toString().slice(0, 200);

        this.tableSettingSaveSub?.unsubscribe();
        this.tableSettingSaveSub = this.api
            .updateGeneric(this.apiBaseUrl + 'tabelle_einstellung/update.php', tabellenEinstellung)
            .subscribe({
                next: () => {},
                error: err => {
                    this.messageService.errorMessage('Tabelleneinstellungen konnten nicht gespeichert werden! ', err);
                },
            });
    }

    loadTableSettings() {
        if (!this.tableDescriptor.uniqueIdentifier) {
            this.loadVisibleColumns();
        } else {
            const params = new HttpParams().set('tabelle', this.tableDescriptor.uniqueIdentifier);
            this.tableSettingSub?.unsubscribe();
            this.tableSettingSub = this.api
                .get(this.apiBaseUrl + 'tabelle_einstellung/read_einstellung.php', params)
                .subscribe({
                    next: (value: TableSetting) => {
                        if (value) {
                            if (this.tableDescriptor.persistPageIndex) {
                                this.paginator.pageIndex = value.seitenindex || 0;
                            }
                            this.paginator.pageSize = value.elemente_pro_seite || 10;
                            this.searchCtrl.setValue(this.tableDescriptor.clearSearch ? '' : value.suchtext || '', {
                                emitEvent: false,
                            });
                            if (value.sortierung && !value.sortierung?.includes(',')) {
                                const [active, direction] = value.sortierung.split(' ');
                                this.sort.active = active;
                                this.sort.direction = direction as SortDirection;
                            } else {
                                this.sort.active = this.tableDescriptor.initialSortColumn;
                                this.sort.direction = this.tableDescriptor.initialSortDirection as SortDirection;
                            }
                        }
                        this.loadVisibleColumns();
                    },
                    error: () => {
                        this.loadVisibleColumns();
                    },
                });
        }
    }

    private getSortColumns(): string[] {
        if (this.sort?.active != null) {
            for (const tc of this.tableDescriptor.columns) {
                if (this.sort.active === tc.name && tc.isActive) {
                    if (tc.sortFields?.length > 0) {
                        return tc.sortFields;
                    }
                }
            }
            return [this.sort.active];
        }
        return [this.tableDescriptor.initialSortColumn];
    }

    private sortCurrentData(): void {
        if (!this.sort?.active || !this.sort?.direction) {
            return;
        }
        const direction = this.sort.direction === 'asc' ? 1 : -1;
        const sortColumns = this.getSortColumns();
        const sortField = sortColumns?.length ? sortColumns[0] : this.sort.active;
        const data = [...(this.tableDataSource.data as any[])];
        data.sort((a, b) => {
            const aValue = this.getSortableValue(a?.[sortField]);
            const bValue = this.getSortableValue(b?.[sortField]);
            if (aValue < bValue) {
                return -1 * direction;
            }
            if (aValue > bValue) {
                return 1 * direction;
            }
            return 0;
        });
        this.tableDataSource.data = data;
    }

    private getSortableValue(value: any): string | number {
        if (value == null) {
            return '';
        }
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            const asNumber = Number(value);
            if (!Number.isNaN(asNumber) && value.trim() !== '') {
                return asNumber;
            }
            return value.toLowerCase();
        }
        return value.toString().toLowerCase();
    }

    private applyClientSideSearch(searchText: string): void {
        const search = (searchText || '').toString().trim().toLowerCase();
        if (!search) {
            this.filteredClientData = [...this.unfilteredData];
            this.totalCount = this.filteredClientData.length;
            this.updateClientSideVisibleData();
            return;
        }
        this.filteredClientData = this.unfilteredData.filter(row => this.matchesSearch(row, search));
        this.totalCount = this.filteredClientData.length;
        this.updateClientSideVisibleData();
    }

    private matchesSearch(row: any, search: string): boolean {
        if (!row) {
            return false;
        }
        for (const key of Object.keys(row)) {
            const value = row[key];
            if (value === null || value === undefined) {
                continue;
            }
            const asText = String(value).toLowerCase();
            if (asText.includes(search)) {
                return true;
            }
        }
        return false;
    }

    private isPagePastEnd(response: KieneApi): boolean {
        const pageIndex = this.paginator?.pageIndex || 0;
        if (pageIndex <= 0) {
            return false;
        }
        const pageSize = this.paginator?.pageSize || this.tableDescriptor.defaultPageSize || 0;
        const countFromApi = Number(response?.count);
        const count = Number.isFinite(countFromApi) ? countFromApi : response?.records?.length || 0;
        if (pageSize > 0 && pageIndex * pageSize >= count) {
            return true;
        }
        return !this.tableDescriptor.clientSideSearch && (response?.records?.length || 0) === 0;
    }

    private updateClientSideVisibleData(): void {
        const pageSize = this.paginator?.pageSize || this.tableDescriptor.defaultPageSize || 100;
        let pageIndex = this.paginator?.pageIndex || 0;
        const length = this.filteredClientData?.length || 0;
        const maxPageIndex = length === 0 || pageSize <= 0 ? 0 : Math.ceil(length / pageSize) - 1;
        if (pageIndex > maxPageIndex) {
            pageIndex = 0;
            if (this.paginator) {
                this.paginator.pageIndex = 0;
            }
        }
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        this.tableDataSource.data = this.filteredClientData.slice(start, end);
    }

    private loadVisibleColumns(): void {
        const identifier = this.getColumnFilterIdentifier(false);
        if (identifier) {
            const url = this.apiBaseUrl + 'tabelle_spalte/read.php?tabelle=' + identifier;
            this.api.get(url).subscribe({
                next: value => {
                    this.initSpaltenKonfigurationen(true, value.records);
                    this.loadVisibleExpandableColumns();
                },
                error: err => {
                    this.initSpaltenKonfigurationen(true);
                    this.loadVisibleExpandableColumns();
                    this.messageService.errorMessage('Fehler beim Laden der Spalten!', err);
                },
            });
        } else {
            this.initSpaltenKonfigurationen(true);
            this.loadVisibleExpandableColumns();
        }
    }

    private loadVisibleExpandableColumns(): void {
        if (!this.tableDescriptor?.expandable) {
            this.expandableSpaltenKonfigurationen = [];
            return;
        }
        const identifier = this.getColumnFilterIdentifier(true);
        if (!identifier) {
            this.initExpandableSpaltenKonfigurationen(false);
            return;
        }
        const url = this.apiBaseUrl + 'tabelle_spalte/read.php?tabelle=' + identifier;
        this.api.get(url).subscribe({
            next: value => {
                this.initExpandableSpaltenKonfigurationen(false, value.records);
            },
            error: () => {
                this.initExpandableSpaltenKonfigurationen(false);
            },
        });
    }

    private initExpandableSpaltenKonfigurationen(loadData: boolean, spalteKonfigAusDb?: SpalteKonfiguration[]) {
        if (spalteKonfigAusDb) {
            this.savedExpandableSpaltenKonfigurationen = spalteKonfigAusDb;
        }
        const konfigs = [];
        for (const col of this.tableDescriptor?.expandableColumns || []) {
            const sk = new SpalteKonfiguration();
            sk.spalte = col.name;
            sk.titel = col.title;
            sk.aktiv = col.isActive;
            sk.sichtbar = col.isActive === true ? 1 : 0;
            this.applySavedSpaltenSichtbarkeit(sk, spalteKonfigAusDb);
            konfigs.push(sk);
        }
        this.expandableSpaltenKonfigurationen = konfigs;
        if (loadData) {
            this.loadDataNew('init expandable', false);
        }
    }

    private initSpaltenKonfigurationen(loadData: boolean, spalteKonfigAusDb?: SpalteKonfiguration[]) {
        if (spalteKonfigAusDb) {
            this.savedSpaltenKonfigurationen = spalteKonfigAusDb;
        }
        const konfigs = [];
        for (const col of this.tableDescriptor?.columns) {
            const sk = new SpalteKonfiguration();
            sk.spalte = col.name;
            sk.titel = col.title;
            sk.aktiv = col.isActive;
            sk.sichtbar = col.isActive === true ? 1 : 0;
            if (col.type == 'array') {
                const ak: ArrayTableColumn = col;
                sk.arrayKey = col.displayFields[0];
            }

            this.applySavedSpaltenSichtbarkeit(sk, spalteKonfigAusDb);

            konfigs.push(sk);
        }

        this.spaltenKonfigurationen = konfigs;
        this.initialDisplayedColumns('loadVisibleColumns #2', loadData);
    }

    private applySavedSpaltenSichtbarkeit(sk: SpalteKonfiguration, spalteKonfigAusDb?: SpalteKonfiguration[]): void {
        if (!spalteKonfigAusDb || !sk.aktiv) {
            return;
        }
        const ausDb = spalteKonfigAusDb.find(s => s.spalte === sk.spalte);
        if (ausDb != null) {
            // Normalize to 0|1 number (API may return string "0"/"1").
            sk.sichtbar = Number(ausDb.sichtbar) > 0 ? 1 : 0;
        }
    }

    /**
     * nur von initSpaltenKonfigurationen aufrufen!
     * @param sender
     * @param loadData
     */
    private initialDisplayedColumns(sender: string, loadData: boolean): void {
        if (!environment?.production) {
            console.log('call initialDisplayedColumns from: ' + sender);
        }
        const cols = [];

        if (this.localStorageService?.isCurrentUserPhitoUser()) {
            cols.push('entityViewerButton');
        }

        if (this.tableDescriptor?.draggable) {
            cols.push('dragHandle');
        }

        if (this.tableDescriptor?.selectable) {
            cols.push('select');
        }

        if (this.tableDescriptor?.expandable) {
            cols.push('detailsButton');
        }

        for (const sk of this.spaltenKonfigurationen) {
            if (sk.sichtbar > 0 && sk.aktiv) {
                cols.push(sk.spalte);
            }
        }

        if (this.tableDescriptor?.options?.length > 0) {
            cols.push('options');
        }

        this.displayedColumns = cols;
        this.scheduleHorizontalScrollbarSync();

        if (loadData) {
            this.loadDataNew('init', false);
        }
    }

    getExpandableColumns(): TableColumn[] {
        if (!this.tableDescriptor?.expandable) {
            return [];
        }
        if (!this.expandableSpaltenKonfigurationen?.length) {
            return this.tableDescriptor.expandableColumns || [];
        }
        const visibleNames = new Set(
            this.expandableSpaltenKonfigurationen.filter(sk => sk.sichtbar > 0 && sk.aktiv).map(sk => sk.spalte),
        );
        return (this.tableDescriptor.expandableColumns || []).filter(col => visibleNames.has(col.name));
    }

    private getColumnFilterIdentifier(forExpandableColumns: boolean): string | null {
        if (!this.tableDescriptor?.uniqueIdentifier) {
            return null;
        }
        if (forExpandableColumns) {
            return this.tableDescriptor.uniqueIdentifier + '_details';
        }
        return this.tableDescriptor.uniqueIdentifier;
    }

    private isKeyEventFromInput(event: KeyboardEvent): boolean {
        if (event.target instanceof Node) {
            switch (event.target.nodeName) {
                case 'INPUT':
                case 'SELECT':
                case 'TEXTAREA':
                    return true;
                default:
                    return false;
            }
        }
        return false;
    }

    private getUrl() {
        if (this.tableDescriptor.alternativeApiUrl && !this.tableDescriptor.apiUrl) {
            return this.tableDescriptor.alternativeApiUrl;
        }

        return this.tableDescriptor.apiUrl + 'read.php';
    }

    onTableHorizontalScroll() {
        if (this.syncFromBottom) {
            return;
        }
        const tableEl = this.tableScrollContainer?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        this.syncFromTable = true;
        bottomEl.scrollLeft = tableEl.scrollLeft;
        this.syncFromTable = false;
        this.applyFloatingHeaderTransform(tableEl.scrollLeft);
    }

    onBottomScrollbarScroll() {
        if (this.syncFromTable) {
            return;
        }
        const tableEl = this.tableScrollContainer?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }
        this.syncFromBottom = true;
        tableEl.scrollLeft = bottomEl.scrollLeft;
        this.syncFromBottom = false;
        this.applyFloatingHeaderTransform(tableEl.scrollLeft);
    }

    @HostListener('window:resize')
    onWindowResize() {
        this.stickyHeaderCellsBuilt = false;
        this.syncHorizontalScrollbar();
        this.updateFloatingStickyHeader(true);
    }

    private initFloatingStickyHeader() {
        if (!this.tableDescriptor?.stickyHeader) {
            return;
        }
        this.stickyHeaderScrollParent =
            this.hostElement.nativeElement.closest('mat-sidenav-content') ||
            (this.document.scrollingElement as HTMLElement) ||
            this.document.documentElement;
        this.stickyHeaderScrollParent?.addEventListener('scroll', this.onStickyHeaderScroll, { passive: true });
        requestAnimationFrame(() => this.updateFloatingStickyHeader(true));
        setTimeout(() => this.updateFloatingStickyHeader(true), 80);
    }

    private destroyFloatingStickyHeader() {
        this.stickyHeaderScrollParent?.removeEventListener('scroll', this.onStickyHeaderScroll);
        this.stickyHeaderScrollParent = null;
        if (this.stickyHeaderRaf) {
            cancelAnimationFrame(this.stickyHeaderRaf);
            this.stickyHeaderRaf = 0;
        }
        this.stickyHeaderVisible = false;
        this.stickyHeaderCellsBuilt = false;
    }

    private scheduleFloatingStickyHeaderUpdate() {
        if (this.stickyHeaderRaf) {
            return;
        }
        this.stickyHeaderRaf = requestAnimationFrame(() => {
            this.stickyHeaderRaf = 0;
            this.updateFloatingStickyHeader(false);
        });
    }

    private applyFloatingHeaderTransform(scrollLeft: number) {
        const inner = this.floatingStickyHeaderInner?.nativeElement;
        if (inner) {
            inner.style.transform = `translate3d(${-scrollLeft}px, 0, 0)`;
        }
    }

    private applyFloatingHeaderBox(
        top: number,
        left: number,
        width: number,
        height: number,
        scrollLeft: number,
    ) {
        const bar = this.floatingStickyHeader?.nativeElement;
        if (bar) {
            bar.style.top = `${top}px`;
            bar.style.left = `${left}px`;
            bar.style.width = `${width}px`;
            bar.style.height = `${height}px`;
        }
        const inner = this.floatingStickyHeaderInner?.nativeElement;
        if (inner) {
            inner.style.height = `${height}px`;
            inner.style.transform = `translate3d(${-scrollLeft}px, 0, 0)`;
        }
    }

    private rebuildFloatingHeaderCells(headerRow: HTMLElement, headerHeight: number) {
        const ths = Array.from(headerRow.querySelectorAll('th')) as HTMLElement[];
        this.stickyHeaderHeight = headerHeight;
        this.stickyHeaderCells = ths.map(th => ({
            label: (th.innerText || '').replace(/\s+/g, ' ').trim(),
            width: th.getBoundingClientRect().width,
            isOptions: th.classList.contains('mat-column-options') || th.classList.contains('cdk-column-options'),
        }));
        this.stickyHeaderCellsBuilt = true;
        this.cdr.detectChanges();
    }

    private updateFloatingStickyHeader(forceRebuildCells: boolean) {
        if (!this.tableDescriptor?.stickyHeader) {
            if (this.stickyHeaderVisible) {
                this.stickyHeaderVisible = false;
                this.cdr.markForCheck();
            }
            return;
        }

        const container = this.tableScrollContainer?.nativeElement;
        const tableEl = this.table?.nativeElement || container?.querySelector('table.kiene-table');
        const headerRow = container?.querySelector(
            'tr.mat-header-row, tr.mat-mdc-header-row',
        ) as HTMLElement | null;
        if (!container || !tableEl || !headerRow) {
            return;
        }

        const toolbar = this.document.querySelector('.kiene-toolbar') as HTMLElement | null;
        const toolbarBottom = toolbar ? toolbar.getBoundingClientRect().bottom : 0;
        const headerRect = headerRow.getBoundingClientRect();
        const tableRect = (tableEl as HTMLElement).getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const headerHeight = Math.max(headerRect.height, 40);

        const shouldShow =
            headerRect.top <= toolbarBottom && tableRect.bottom > toolbarBottom + headerHeight;

        if (shouldShow && (forceRebuildCells || !this.stickyHeaderCellsBuilt)) {
            this.rebuildFloatingHeaderCells(headerRow, headerHeight);
        }

        this.applyFloatingHeaderBox(
            toolbarBottom,
            containerRect.left,
            containerRect.width,
            headerHeight,
            container.scrollLeft,
        );

        if (this.stickyHeaderVisible !== shouldShow) {
            this.stickyHeaderVisible = shouldShow;
            this.cdr.detectChanges();
        }
    }

    private syncHorizontalScrollbar() {
        const tableEl = this.tableScrollContainer?.nativeElement;
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!tableEl || !bottomEl) {
            return;
        }

        this.horizontalScrollWidth = tableEl.scrollWidth;
        const overflowWidth = tableEl.scrollWidth - tableEl.clientWidth;
        this.showHorizontalScrollbar = overflowWidth > 0.5;
        bottomEl.scrollLeft = tableEl.scrollLeft;
        this.updateFloatingStickyHeader(false);
    }

    private scheduleHorizontalScrollbarSync() {
        requestAnimationFrame(() => this.syncHorizontalScrollbar());
        setTimeout(() => this.syncHorizontalScrollbar(), 60);
    }

    private initHorizontalScrollbarObserver() {
        const tableEl = this.tableScrollContainer?.nativeElement;
        const nativeTable = this.table?.nativeElement;
        if (!tableEl || typeof ResizeObserver === 'undefined') {
            return;
        }
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(() => {
            this.syncHorizontalScrollbar();
        });
        this.resizeObserver.observe(tableEl);
        if (nativeTable) {
            this.resizeObserver.observe(nativeTable);
        }
    }

    private mountBottomScrollbarToBody() {
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!bottomEl || this.bottomScrollbarMountedToBody) {
            return;
        }
        this.renderer.appendChild(this.document.body, bottomEl);
        this.bottomScrollbarMountedToBody = true;
    }

    private unmountBottomScrollbarFromBody() {
        const bottomEl = this.bottomScrollbar?.nativeElement;
        if (!bottomEl || !this.bottomScrollbarMountedToBody) {
            return;
        }
        this.renderer.removeChild(this.document.body, bottomEl);
        this.bottomScrollbarMountedToBody = false;
    }

    private getParams(withLimitAndOffset: boolean): HttpParams {
        let params: HttpParams = new HttpParams();
        if (withLimitAndOffset && !this.tableDescriptor.clientSideSearch) {
            const offset =
                this.paginator.pageIndex != null && this.paginator.pageSize != null
                    ? this.paginator.pageIndex * this.paginator.pageSize
                    : 0;
            const limit =
                this.paginator.pageSize != null ? this.paginator.pageSize : this.tableDescriptor.defaultPageSize;
            params = params.set('limit', limit.toString());
            params = params.set('offset', offset.toString());
        }

        params = params.set('orderby', this.getOrderBy());
        params = params.set('search', this.searchCtrl.value ? this.searchCtrl.value : '');

        let belegeOderFinanzen = false;

        const currentUrl = this.router.url;
        if (currentUrl && (currentUrl.startsWith('/belege/') || currentUrl.startsWith('/finanzen/'))) {
            belegeOderFinanzen = true;
        }
        let ausklappen = false;

        if (belegeOderFinanzen || this.tableDescriptor.sendCurrentClient) {
            if (this.kunde_id) {
                params = params.set('kunde_id[]', this.kunde_id);
            } else if (this.localStorageService.getCurrentClient()?.kunde_id) {
                params = params.set('kunde_id[]', this.localStorageService.getCurrentClient().kunde_id.toString());
                ausklappen = true;
            }
        }

        if (currentUrl && currentUrl.startsWith('/stammdaten/betriebe')) {
            ausklappen = true;
        }

        params = this.getTableFilterParams(params);

        if (this.tableDescriptor.params) {
            const keys: string[] = this.tableDescriptor.params.keys();
            for (const k of keys) {
                const values: string[] = this.tableDescriptor.params.getAll(k);
                for (const v of values) {
                    params = params.append(k, v);
                }
            }
        }

        if (this.tableDescriptor.filterParams) {
            const keys: string[] = this.tableDescriptor.filterParams.keys();
            for (const k of keys) {
                const values: string[] = this.tableDescriptor.filterParams.getAll(k);
                for (const v of values) {
                    params = params.append(k, v);
                }
            }
        }

        if (this.externalParams) {
            const keys = this.externalParams.keys();
            for (const key of keys) {
                const value = this.externalParams.get(key);
                params = params.delete(key);
                params = params.set(key, value);
            }
        }

        return params;
    }

    private applyAutoExpandRows(records: any[]) {
        if (!this.shouldAutoExpandRows()) {
            return;
        }
        if (this.tableDescriptor.openFirstRows <= 0 || this.expandedIndexes.length > 0) {
            return;
        }
        if (!Array.isArray(records) || records.length === 0) {
            return;
        }

        const rowsToOpen = Math.min(this.tableDescriptor.openFirstRows, records.length);
        for (let i = 0; i < rowsToOpen; i++) {
            const row = records[i];
            const key = this.getExpandKeyFromRow(row, i);
            if (key !== null && !this.expandedIndexes.includes(key)) {
                this.expandedIndexes.push(key);
            }
        }
    }

    private shouldAutoExpandRows(): boolean {
        const currentUrl = this.router.url;
        const isBelegeOderFinanzenRoute =
            !!currentUrl && (currentUrl.startsWith('/belege/') || currentUrl.startsWith('/finanzen/'));
        if (isBelegeOderFinanzenRoute || this.tableDescriptor.sendCurrentClient) {
            return !!(this.kunde_id || this.localStorageService.getCurrentClient()?.kunde_id);
        }
        if (currentUrl && currentUrl.startsWith('/stammdaten/betriebe')) {
            return true;
        }
        return false;
    }

    private getExpandKeyFromRow(row: any, fallbackIndex: number): string | number | null {
        const key = this.getExpandKey(row);
        if (key !== null) {
            return key;
        }
        return fallbackIndex;
    }

    private prepareColumnsForDownloads() {
        let columns = '';
        for (const col of this.spaltenKonfigurationen) {
            // Use > 0 like displayedColumns: mysqli/JSON may return sichtbar as "0"/"1",
            // and the string "0" is truthy in JS (would export hidden columns).
            if (col.aktiv && col.sichtbar > 0) {
                columns += col.spalte;
                if (col.arrayKey) {
                    columns += '.' + col.arrayKey;
                }
                columns += '::' + col.titel + ';';
            }
        }
        if (columns.length > 0) {
            columns = columns.substr(0, columns.length - 1);
        }
        return columns;
    }

    public emitActionEvent(row, tc: TableColumn): void {
        const atc = <ActionTableColumn>tc;
        if (atc.void) {
            if (this.hasActionPermission(atc)) {
                atc.void(row);
            }
        }
    }

    clickedToolbar(tb: ToolbarButton) {
        if (tb.void) {
            tb.void();
        }
    }

    hasActionPermission(c: ActionTableColumn): boolean {
        if (!c?.benutzerrechtIdAction) {
            return true;
        }
        return this.localStorageService.hasPermission(c.benutzerrechtIdAction);
    }

    hasViewPermission(c: TableColumn): boolean {
        const atc = <ActionTableColumn>c;
        if (!atc?.benutzerrechtIdView) {
            return true;
        }
        return this.localStorageService.hasPermission(atc.benutzerrechtIdView);
    }
}
